import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

const DEFAULT_FROM_NAME = 'TAILORED PACKAGING, LLC';
const DEFAULT_FROM_ADDRESS = '1380 INDUSTRIAL PARK DRIVE\nEDMORE, MI 48829';

const genBolNum = () => {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const rnd = String(Math.floor(Math.random() * 900) + 100);
  return `BOL-${ymd}-${rnd}`;
};

export default function BillOfLading() {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [shipDate, setShipDate] = useState(new Date().toISOString().slice(0, 10));
  const [carrier, setCarrier] = useState('');
  const [bolNum, setBolNum] = useState(genBolNum());
  const [shipVia, setShipVia] = useState('Our Truck');
  const [terms, setTerms] = useState('F.O.B. 30 Net');
  const [salesperson, setSalesperson] = useState('');
  const [shipToAddress, setShipToAddress] = useState('');
  const [totalWeight, setTotalWeight] = useState('');
  const [instructions, setInstructions] = useState('');
  const [fromName, setFromName] = useState(DEFAULT_FROM_NAME);
  const [fromAddress, setFromAddress] = useState(DEFAULT_FROM_ADDRESS);
  const [items, setItems] = useState([]); // {order_id, qty_to_ship, skid_groups: [{qty_per_skid, num_skids}]}
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/api/customers').then(r => setCustomers(r.data || []));
  }, []);
  useEffect(() => {
    if (!customerId) return setOrders([]);
    api.get('/api/orders?archive=Active').then(r => setOrders((r.data || []).filter(o => String(o.customer_id) === String(customerId) && o.status !== 'Delivered')));
  }, [customerId]);
  useEffect(() => {
    if (!customerId) { setShipToAddress(''); return; }
    const c = customers.find(x => String(x.id) === String(customerId));
    setShipToAddress(c?.default_ship_to_address || '');
  }, [customerId, customers]);

  const customer = customers.find(c => String(c.id) === String(customerId));

  const addItem = (order) => {
    if (items.find(i => i.order_id === order.id)) return;
    setItems([...items, { order_id: order.id, part_number: order.part_number, description: order.part_description || '', po_number: order.po_number, qty_to_ship: 0, skid_groups: [{ qty_per_skid: 0, num_skids: 1 }] }]);
  };
  const updItem = (idx, patch) => setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const addSkidRow = (idx) => updItem(idx, { skid_groups: [...items[idx].skid_groups, { qty_per_skid: 0, num_skids: 1 }] });
  const updSkid = (idx, sIdx, patch) => updItem(idx, {
    skid_groups: items[idx].skid_groups.map((s, i) => i === sIdx ? { ...s, ...patch } : s)
  });
  const removeSkid = (idx, sIdx) => updItem(idx, { skid_groups: items[idx].skid_groups.filter((_, i) => i !== sIdx) });

  const totals = useMemo(() => {
    let totalSkids = 0, totalUnits = 0;
    items.forEach(it => it.skid_groups.forEach(s => {
      totalSkids += Number(s.num_skids || 0);
      totalUnits += Number(s.qty_per_skid || 0) * Number(s.num_skids || 0);
    }));
    return { totalSkids, totalUnits };
  }, [items]);

  const save = async () => {
    setMsg('');
    try {
      await api.post('/api/bols', {
        bol_number: bolNum, customer_id: customerId, ship_date: shipDate, carrier, special_instructions: instructions,
        items: items.map(it => ({ order_id: it.order_id, qty_to_ship: it.qty_to_ship, skid_groups: it.skid_groups })),
      });
      setMsg('BOL saved.');
    } catch { setMsg('Failed to save BOL.'); }
  };

  const printBol = () => window.print();
  const printLoadTags = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    let html = `<html><head><title>Load Tags</title><style>
      @page { margin: 0.25in; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; }
      .tag-page { page-break-after: always; height: 100vh; display: flex; flex-direction: column; }
      .fold-area { height: 30vh; }
      .fold-line { height: 3vh; display: flex; align-items: center; justify-content: center; gap: 8px; }
      .fold-line .dashes { flex: 1; border-top: 3px dashed #000; }
      .fold-line .fold-text { font-size: 14px; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; white-space: nowrap; }
      .content { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 16px; }
      .content .field { font-size: 48px; font-weight: 800; margin: 6px 0; line-height: 1.2; }
      .content .field .lbl { font-size: 28px; font-weight: 600; }
      .content .skid-label { font-size: 60px; font-weight: 900; margin-top: 12px; line-height: 1.1; }
    </style></head><body>`;
    items.forEach(it => {
      let skidNum = 0;
      const totalForItem = it.skid_groups.reduce((a, s) => a + Number(s.num_skids || 0), 0);
      it.skid_groups.forEach(g => {
        for (let i = 0; i < Number(g.num_skids || 0); i++) {
          skidNum++;
          html += `
            <div class="tag-page">
              <div class="fold-area"></div>
              <div class="fold-line">
                <div class="dashes"></div>
                <div class="fold-text">\u25BC FOLD HERE \u25BC</div>
                <div class="dashes"></div>
              </div>
              <div class="content">
                <div class="field"><span class="lbl">Customer:</span> ${customer?.name || ''}</div>
                <div class="field"><span class="lbl">PO#:</span> ${it.po_number}</div>
                <div class="field"><span class="lbl">Part:</span> ${it.part_number}</div>
                <div class="field"><span class="lbl">Qty:</span> ${g.qty_per_skid}</div>
                <div class="field"><span class="lbl">Date:</span> ${shipDate}</div>
                <div class="skid-label">SKID ${skidNum} OF ${totalForItem}</div>
              </div>
            </div>`;
        }
      });
    });
    html += `</body></html>`;
    w.document.write(html); w.document.close(); w.print();
  };

  const bolPreviewBody = (copyLabel) => (
    <>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#555' }}>Original, Non-Negotiable</div>
        <h2 style={{ margin: '4px 0' }}>Bill of Lading</h2>
        <div style={{ fontSize: 14, fontWeight: 600 }}>BOL #: {bolNum}</div>
      </div>

      <hr style={{ border: 'none', borderTop: '2px solid #000', margin: '10px 0' }} />

      <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#555', marginBottom: 2 }}>From</div>
          <div style={{ fontWeight: 600 }}>{fromName || DEFAULT_FROM_NAME}</div>
          <div style={{ whiteSpace: 'pre-line' }}>{fromAddress || DEFAULT_FROM_ADDRESS}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#555', marginBottom: 2 }}>Sold To</div>
          <div style={{ fontWeight: 600 }}>{customer?.name || '—'}</div>
          <div style={{ whiteSpace: 'pre-line' }}>{customer?.address || '—'}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#555', marginBottom: 2 }}>Ship To</div>
          <div style={{ whiteSpace: 'pre-line' }}>{shipToAddress || customer?.address || '—'}</div>
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '10px 0' }} />

      <div style={{ display: 'flex', gap: 24, marginBottom: 10, fontSize: 13 }}>
        <div><b>Date:</b> {shipDate}</div>
        <div><b>Ship Via:</b> {shipVia}</div>
        <div><b>Terms:</b> {terms}</div>
        {salesperson && <div><b>Salesperson:</b> {salesperson}</div>}
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '10px 0' }} />

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #000' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px' }}>PO #</th>
            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Part #</th>
            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Description</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Skids</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Qty/Skid</th>
            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Total Pcs</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => {
            return it.skid_groups.map((g, gIdx) => {
              const numSkids = Number(g.num_skids || 0);
              const qtyPerSkid = Number(g.qty_per_skid || 0);
              return (
                <tr key={`${it.order_id}-${gIdx}`} style={{ borderBottom: gIdx === it.skid_groups.length - 1 ? '1px solid #ddd' : 'none' }}>
                  <td style={{ padding: '4px 8px' }}>{it.po_number}</td>
                  <td style={{ padding: '4px 8px' }}>{it.part_number}</td>
                  <td style={{ padding: '4px 8px' }}>{it.description}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{numSkids}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{qtyPerSkid}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{numSkids * qtyPerSkid}</td>
                </tr>
              );
            });
          })}
        </tbody>
      </table>

      <div style={{ display: 'flex', gap: 24, marginTop: 12, fontWeight: 600 }}>
        <div>Total Pieces: {totals.totalUnits}</div>
        <div>Total Skids: {totals.totalSkids}</div>
        <div>Total Weight: {totalWeight ? `${totalWeight} lbs` : '__________ lbs'}</div>
      </div>

      {instructions && (
        <div style={{ marginTop: 12, padding: 8, background: '#f9f9f9', border: '1px solid #ddd', borderRadius: 4 }}>
          <b>Special Instructions:</b> {instructions}
        </div>
      )}

      <div style={{ marginTop: 24, fontSize: 12, display: 'flex', gap: 24, alignItems: 'flex-end' }}>
        <div style={{ flex: 2 }}>
          <b>Received By:</b>
          <div style={{ borderBottom: '1px solid #000', height: 20, marginTop: 2 }}></div>
        </div>
        <div style={{ flex: 1 }}>
          <b>Date:</b>
          <div style={{ borderBottom: '1px solid #000', height: 20, marginTop: 2 }}></div>
        </div>
      </div>

      <div style={{ marginTop: 16, border: '1px solid #000', padding: 10, fontSize: 10, lineHeight: 1.35, textAlign: 'left' }}>
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>
          SHORTAGE OR DAMAGES SHOULD BE NOTED ON RECEIPT OF SHIPMENT AND VERIFIED WITH SIGNATURE BY {(fromName || DEFAULT_FROM_NAME).toUpperCase()} REPRESENTATIVES, OTHERWISE CLAIMS WILL NOT BE ALLOWED
        </div>
        <p style={{ margin: '6px 0' }}>
          The property described below, in apparent good order except as noted (contents and condition of contents of package unknown), marked, consigned and destined as indicated below. Which said carrier (the word carrier being understood throughout this contract as meaning any person or corporation in possession of the property under the contract) agrees to carry to its usual place of delivery at said destination, if on its route, otherwise to deliver to another carrier on the route to said destination.
        </p>
        <p style={{ margin: '6px 0' }}>
          It is mutually agreed as to each carrier of all or any said property over all or any portion of said route or destination, and as to each party at any time interested in all or any of said property, that every service to be performed hereunder shall be subject to all terms and conditions of the Uniform Straight Bill of Lading set forth:
          (1) in Uniform Freight Classification in effect on the date hereof, if this is a rail or rail-water shipment, or
          (2) in the applicable motor carrier classification or tariff if this is a motor carrier shipment.
        </p>
        <p style={{ margin: '6px 0' }}>
          Shipper hereby certifies that he is familiar with the terms and conditions of the said bill of lading, including those on the back hereof, set forth in the classifications or tariff which governs the transportation of the shipment, and said terms and conditions are hereby agreed to by the shipper and accepted for himself and his assigns.
        </p>
      </div>

      <div className="copy-label">— {copyLabel} —</div>
    </>
  );

  return (
    <div>
      <h2>Bill of Lading</h2>
      <div className="bol-layout">
        <div className="card no-print">
          <div className="field"><label>Customer</label>
            <select value={customerId} onChange={e => setCustomerId(e.target.value)}>
              <option value="">Select…</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid2">
            <div className="field"><label>Ship Date</label><input type="date" value={shipDate} onChange={e => setShipDate(e.target.value)} /></div>
            <div className="field"><label>BOL #</label><input value={bolNum} onChange={e => setBolNum(e.target.value)} /></div>
            <div className="field"><label>Ship Via</label><input value={shipVia} onChange={e => setShipVia(e.target.value)} /></div>
            <div className="field"><label>Carrier</label><input value={carrier} onChange={e => setCarrier(e.target.value)} /></div>
            <div className="field"><label>Terms</label><input value={terms} onChange={e => setTerms(e.target.value)} /></div>
            <div className="field"><label>Salesperson</label><input value={salesperson} onChange={e => setSalesperson(e.target.value)} /></div>
            <div className="field"><label>Total Weight (lbs)</label><input type="number" value={totalWeight} onChange={e => setTotalWeight(e.target.value)} placeholder="Leave blank for manual entry" /></div>
          </div>
          <div className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ flex: 1, marginBottom: 0 }}>From (shipper name on the BOL)</label>
            <button
              type="button"
              onClick={() => { setFromName(DEFAULT_FROM_NAME); setFromAddress(DEFAULT_FROM_ADDRESS); }}
              style={{ padding: '4px 10px', fontSize: 12 }}
            >Reset to Tailored Packaging</button>
          </div>
          <div className="grid2">
            <div className="field"><label>From Name</label><input value={fromName} onChange={e => setFromName(e.target.value)} placeholder={DEFAULT_FROM_NAME} /></div>
            <div className="field"><label>From Address</label><textarea rows={2} value={fromAddress} onChange={e => setFromAddress(e.target.value)} placeholder={DEFAULT_FROM_ADDRESS} /></div>
          </div>
          <div className="field"><label>Ship To Address</label><textarea rows={2} value={shipToAddress} onChange={e => setShipToAddress(e.target.value)} placeholder="Enter ship-to address if different from customer address" /></div>
          <div className="field"><label>Special Instructions</label><textarea value={instructions} onChange={e => setInstructions(e.target.value)} /></div>

          <h4>Open Orders</h4>
          <div style={{ maxHeight: 160, overflow: 'auto', border: '1px solid #eee', borderRadius: 4 }}>
            {orders.map(o => (
              <div key={o.id} style={{ padding: 8, borderBottom: '1px solid #f0f0f0', display: 'flex' }}>
                <span style={{ flex: 1 }}>{o.part_number} — PO {o.po_number}</span>
                <button className="btn-primary" onClick={() => addItem(o)}>Add</button>
              </div>
            ))}
            {orders.length === 0 && <div style={{ padding: 12, color: '#888' }}>Select a customer to see open orders.</div>}
          </div>

          <h4>Items</h4>
          {items.map((it, idx) => (
            <div key={it.order_id} className="card" style={{ marginBottom: 10, background: '#fafbfc' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ flex: 1 }}><b>{it.part_number}</b> — PO {it.po_number}</div>
                <button className="btn-danger" onClick={() => removeItem(idx)}>Remove</button>
              </div>
              <div className="field"><label>Qty to Ship</label><input type="number" value={it.qty_to_ship} onChange={e => updItem(idx, { qty_to_ship: e.target.value })} /></div>
              <div style={{ fontWeight: 600, marginTop: 6 }}>Skid Groups</div>
              <div className="skid-row" style={{ fontSize: 12, color: '#666' }}><div>Qty Per Skid</div><div># of Skids</div><div>Subtotal</div><div></div></div>
              {it.skid_groups.map((s, sIdx) => (
                <div key={sIdx} className="skid-row">
                  <input type="number" value={s.qty_per_skid} onChange={e => updSkid(idx, sIdx, { qty_per_skid: e.target.value })} />
                  <input type="number" value={s.num_skids} onChange={e => updSkid(idx, sIdx, { num_skids: e.target.value })} />
                  <input value={Number(s.qty_per_skid || 0) * Number(s.num_skids || 0)} readOnly />
                  <button className="btn-danger" onClick={() => removeSkid(idx, sIdx)}>×</button>
                </div>
              ))}
              <button className="btn-primary" style={{ marginTop: 6 }} onClick={() => addSkidRow(idx)}>+ Add Row</button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn-success" onClick={save}>Save BOL</button>
            <button className="btn-primary" onClick={printBol}>Print BOL</button>
            <button className="btn-orange" onClick={printLoadTags}>Print Load Tags</button>
          </div>
          {msg && <div style={{ marginTop: 8 }}>{msg}</div>}
        </div>

        <style>{`
          @media screen {
            .vendor-copy, .copy-label { display: none; }
          }
          @media print {
            @page { size: letter; margin: 0.5in; }
            body * { visibility: hidden; }
            .bol-print, .bol-print * { visibility: visible; }
            .bol-print { position: absolute; left: 0; top: 0; width: 100%; }
            .bol-page {
              page-break-after: always;
              break-after: page;
              page-break-inside: avoid;
            }
            .bol-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }
            .copy-label { display: block; text-align: center; font-weight: 700; letter-spacing: 3px; padding-top: 16px; font-size: 14px; text-transform: uppercase; }
          }
        `}</style>
        <div className="bol-print">
          <div className="card bol-page customer-copy" style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: 13, lineHeight: 1.5 }}>
            {bolPreviewBody('CUSTOMER COPY')}
          </div>
          <div className="card bol-page vendor-copy" style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: 13, lineHeight: 1.5 }}>
            {bolPreviewBody('VENDOR COPY')}
          </div>
        </div>
      </div>
    </div>
  );
}
